const PILL_DRAG_THRESHOLD = 5;
const SWATCH_FADE = { duration: 400, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' };
const MOBILE_QUERY = window.matchMedia('(max-width: 989px)');
const MOBILE_ROWS = 2;

class PlaymatDesignPicker extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.updateOverflow = this.updateOverflow.bind(this);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.mode = 'color';
    this.filters = { color: '', collection: '' };
    this.expanded = false;
    this.handleScroll = (event) => this.updateFade(event.target);
    this.addEventListener('click', this.handleClick);
    const items = Array.from(this.querySelectorAll('.design-picker__item'));
    this.querySelectorAll('[data-filter-group]').forEach((group) => {
      group.querySelectorAll('[data-filter-value]').forEach((pill) => {
        pill.hidden = pill.dataset.filterValue !== '' && !items.some((item) =>
          this.itemMatches(item, pill.dataset.filterValue, group.dataset.filterGroup));
        pill.setAttribute('aria-pressed', String(pill.dataset.filterValue === ''));
      });
    });
    this.pillRows.forEach((row) => {
      row.addEventListener('scroll', this.handleScroll, { passive: true });
      row.addEventListener('pointerdown', this.handlePointerDown);
      this.updateFade(row);
    });
    this.setMode(this.mode, { animate: false });
    if (this.querySelector('[data-hide-from-all] input:checked')) {
      Object.keys(this.filters).forEach((group) => this.setFilter(group, 'sale', { smooth: false }));
    }
    this.connectOverflow();
  }

  connectOverflow() {
    this.showAllButton = this.querySelector('[data-show-all]');
    if (!this.showAllButton) return;
    MOBILE_QUERY.addEventListener('change', this.updateOverflow);
    this.personalizationObserver = new MutationObserver(this.updateOverflow);
    this.personalizationObserver.observe(this.closest('variant-picker'), { attributeFilter: ['class'] });
    this.personalizationObserver.observe(this, { subtree: true, attributeFilter: ['data-personalizable'] });
    this.updateOverflow();
  }

  disconnectOverflow() {
    MOBILE_QUERY.removeEventListener('change', this.updateOverflow);
    this.personalizationObserver?.disconnect();
  }

  updateOverflow() {
    if (!this.showAllButton) return;
    const items = Array.from(this.querySelectorAll('.design-picker__item'));
    items.forEach((item) => item.classList.remove('is-overflow'));
    const shown = items.filter((item) => window.getComputedStyle(item).display !== 'none');
    const limit = this.columnCount * MOBILE_ROWS;
    const capped = !this.expanded && MOBILE_QUERY.matches && shown.length > limit;
    if (capped) this.overflowItems(shown, limit).forEach((item) => item.classList.add('is-overflow'));
    this.showAllButton.hidden = !capped;
    this.showAllButton.textContent = this.showAllButton.dataset.label.replace('__count__', shown.length);
  }

  overflowItems(shown, limit) {
    const kept = shown.slice(0, limit);
    const selected = shown.find((item) => item.querySelector('input:checked'));
    if (selected && !kept.includes(selected)) kept[limit - 1] = selected;
    return shown.filter((item) => !kept.includes(item));
  }

  get columnCount() {
    return Number(window.getComputedStyle(this).getPropertyValue('--picker-swatch-columns'));
  }

  expand() {
    const firstRevealed = this.querySelector('.design-picker__item.is-overflow input');
    this.expanded = true;
    this.updateOverflow();
    firstRevealed.focus();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.pillRows.forEach((row) => {
      row.removeEventListener('scroll', this.handleScroll);
      row.removeEventListener('pointerdown', this.handlePointerDown);
    });
    this.disconnectOverflow();
    this.handlePointerUp();
  }

  handlePointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    event.preventDefault();
    const row = event.currentTarget;
    this.drag = { row, startX: event.clientX, startScroll: row.scrollLeft, moved: false };
    window.addEventListener('pointermove', this.handlePointerMove);
    window.addEventListener('pointerup', this.handlePointerUp);
  }

  handlePointerMove(event) {
    const offset = event.clientX - this.drag.startX;
    if (!this.drag.moved && Math.abs(offset) < PILL_DRAG_THRESHOLD) return;
    this.drag.moved = true;
    this.drag.row.classList.add('is-dragging');
    this.drag.row.scrollLeft = this.drag.startScroll - offset;
  }

  handlePointerUp() {
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    if (!this.drag) return;
    this.drag.row.classList.remove('is-dragging');
    this.suppressClick = this.drag.moved;
    this.drag = null;
    setTimeout(() => (this.suppressClick = false));
  }

  get pillRows() {
    return Array.from(this.querySelectorAll('.design-picker__pills'));
  }

  updateFade(row) {
    const atStart = row.scrollLeft <= 1;
    const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
    row.parentElement.classList.toggle('is-scroll-start', atStart);
    row.parentElement.classList.toggle('is-scroll-end', atEnd);
  }

  get state() {
    return { mode: this.mode, filters: { ...this.filters }, expanded: this.expanded };
  }

  applyState(state) {
    if (!state) return;
    this.filters = { ...this.filters, ...state.filters };
    this.expanded = Boolean(state.expanded);
    this.setMode(state.mode, { animate: false });
    Object.entries(this.filters).forEach(([group, value]) => this.setFilter(group, value, { smooth: false }));
  }

  handleClick(event) {
    if (this.suppressClick) return;
    if (event.target.closest('[data-show-all]')) return this.expand();
    const modeButton = event.target.closest('[data-filter-mode]');
    if (modeButton) return this.setMode(modeButton.dataset.filterMode);

    const pill = event.target.closest('[data-filter-value]');
    if (pill) return this.setFilter(pill.closest('[data-filter-group]').dataset.filterGroup, pill.dataset.filterValue);
  }

  setMode(mode, { animate = true } = {}) {
    this.mode = this.querySelector(`[data-filter-group="${mode}"]`) ? mode : 'color';
    this.querySelectorAll('[data-filter-mode]').forEach((button) => {
      const isActive = button.dataset.filterMode === this.mode;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    });
    this.querySelectorAll('[data-filter-group]').forEach((group) => {
      group.hidden = group.dataset.filterGroup !== this.mode ||
        !group.querySelector('[data-filter-value]:not([data-filter-value=""]):not([hidden])');
    });
    this.pillRows.forEach((row) => this.updateFade(row));
    this.applyFilter({ animate });
  }

  setFilter(group, value, { smooth = true } = {}) {
    const pills = Array.from(this.querySelectorAll(`[data-filter-group="${group}"] [data-filter-value]`));
    this.filters[group] = pills.some((pill) => !pill.hidden && pill.dataset.filterValue === value) ? value : '';
    pills.forEach((pill) => {
      const isActive = pill.dataset.filterValue === this.filters[group];
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-pressed', String(isActive));
      if (isActive) this.revealPill(pill, smooth);
    });
    this.applyFilter({ animate: smooth });
  }

  revealPill(pill, smooth) {
    const row = pill.parentElement;
    const target = pill.offsetLeft - (row.clientWidth - pill.offsetWidth) / 2;
    row.scrollTo({ left: Math.max(0, target), behavior: smooth ? 'smooth' : 'auto' });
  }

  applyFilter({ animate = false } = {}) {
    const value = this.filters[this.mode];
    const items = Array.from(this.querySelectorAll('.design-picker__item'));
    items.forEach((item) => {
      item.hidden = value === '' ? item.hasAttribute('data-hide-from-all') : !this.itemMatches(item, value);
    });
    this.updateOverflow();
    if (!animate || this.reducedMotion.matches) return;
    items
      .filter((item) => !item.hidden)
      .forEach((item) => item.animate([{ opacity: 0 }, { opacity: 1 }], SWATCH_FADE));
  }

  itemMatches(item, value, mode = this.mode) {
    if (value === 'sale') return item.hasAttribute('data-hide-from-all');
    if (mode === 'collection') return item.dataset.collection === value;
    return item.dataset.colorGroups.split(' ').includes(value);
  }
}

customElements.define('playmat-design-picker', PlaymatDesignPicker);
